import { AppDebug, AppDebugLogType } from "../app/debug/AppDebug";
import { IToggle } from "../interfaces/IToggle";
import { TextureStatesData } from "../types/types";
import { ButtonBase } from "./ButtonBase";

/**
 * Toggle Button
 * > needs at least image @see toggleImage
 * 
 * use-case:
 * execution order:
 * myButton
 *   .InitBase()
 *   .InitToggle()
 *   .PostInit()
 */
export class ToggleButton
    extends ButtonBase
    implements IToggle {
    /**
     * Toggle view states on top of base button states
     */
    protected selectedTextureDatas: TextureStatesData;

    /**
     * Flag that reports whether button is selected or not selected
     */
    protected isSelected: boolean;


    /**
     * Select toggle is not yet selected
     * @param selected 
     * @param force force flag set and view update even if equal 
     *              (normally dont need to do this, except in cases like first init)
     * @returns this, for chaining
     */
    public SetSelected(selected: boolean, force: boolean = false): ToggleButton {
        // console.log("Toggle::SetSelected", selected)

        if ((this.isSelected != selected) || force) {
            // toggle selection, enable/disable etc
            this.isSelected = selected;
            this.UpdateViewFromSelected();
        }

        return this;
    }

    public IsSelected(): boolean {
        return this.isSelected;
    }

    public Toggle(): void {
        this.SetSelected(!this.isSelected);
    }

    /**
     * 
     */
    public InitToggle(
        selectedTextureDatas: TextureStatesData,
        isSelected: boolean
    ): ToggleButton {
        // console.log("ToggleButton::InitToggle")
        this.selectedTextureDatas = selectedTextureDatas;
        this.isSelected = isSelected;

        return this;
    }

    public override PostInit(hitAreaExtrusion: number = 0): void {
        // set interactivity, this.isInteractive flag and view (this might change, if isSelected=true, later - this is acceptable in trade for code brevity)
        super.PostInit(hitAreaExtrusion);

        // force set (de)selected and the this.isSelected flag/view update
        this.SetSelected(this.isSelected, true);
    }

    public SetSelectedTextureDatas(selectedTextureDatas: TextureStatesData, updateView: boolean = false): void {
        this.selectedTextureDatas = selectedTextureDatas;

        if (updateView) {
            this.UpdateView();
        }
    }

    public override UpdateView(): void {
        this.UpdateViewFromInteractive();
        this.UpdateViewFromSelected();
    }

    /**
     * Update view from change in selected (true/false) state
     * > refreshes view to match data (eg isSelected)
     * NOTE: that 'selected' has higher priority that 'out|over|disabled' default button states
     */
    protected UpdateViewFromSelected(): void {
        // if selected
        if (this.isSelected) {
            if (this.isOver && this.selectedTextureDatas.over) {
                this.view.setTexture(this.selectedTextureDatas.over.texture, this.selectedTextureDatas.over.frame);
            }
            else {
                this.view.setTexture(this.selectedTextureDatas.out.texture, this.selectedTextureDatas.out.frame);
            }
        }
        // if not selected
        // use either 'out' or 'disabled' texture, based on isInteractive
        else {
            // if is interactive
            if (this.isInteractive) {
                if (this.isOver && this.buttonTextureDatas.over) {
                    this.view.setTexture(this.buttonTextureDatas.over.texture, this.buttonTextureDatas.over.frame);
                }
                else {
                    this.view.setTexture(this.buttonTextureDatas.out.texture, this.buttonTextureDatas.out.frame);
                }
            }
            // if disabled
            else {
                // if got disabled texture
                if (this.buttonTextureDatas.disabled != null) {
                    // use disabled texture
                    this.view.setTexture(this.buttonTextureDatas.disabled.texture, this.buttonTextureDatas.disabled.frame);
                }
                // if dont have disabled texture
                else {
                    // use 'out' texture instead
                    this.view.setTexture(this.buttonTextureDatas.out.texture, this.buttonTextureDatas.out.frame);
                }
            }
        }
    }

    /**
     * Update view from change in @see this.isInteractive
     * NOTE: that 'selected' has higher priority that 'out|over|disabled' default button states
     * NOTE: no 'super' call here, as this effectively 'replaces base class functionality'
     */
    protected override UpdateViewFromInteractive(): void {
        // console.log("ToggleButton::UpdateViewFromInteractive", this.isInteractive);

        // was disabled, is enabled
        if (this.isInteractive) {
            // is not selected (because if it is selected, we already using 'selected' texture with priority)
            if (!this.isSelected) {
                // was using disabled texture
                if (this.buttonTextureDatas.disabled != null) {
                    // set 'out' texture
                    this.view.setTexture(this.buttonTextureDatas.out.texture, this.buttonTextureDatas.out.frame);
                }
            }
        }
        // was enabled, is disabled
        else {
            // is not selected
            if (!this.isSelected) {
                // if got disabled texture
                if (this.buttonTextureDatas.disabled != null) {
                    // set disabled texture
                    this.view.setTexture(this.buttonTextureDatas.disabled.texture, this.buttonTextureDatas.disabled.frame);
                }
            }
        }
    }

    protected override OnPointerOver() {
        // console.log("ToggleButton::OnPointerOver");
        this.isOver = true;

        // default button behaviour
        if (!this.isSelected) {
            if (this.isInteractive) {
                this.view.setTexture(this.buttonTextureDatas.over.texture, this.buttonTextureDatas.over.frame);
            }
        }
        // toggle behaviour
        else {
            if (this.isInteractive) {
                if (this.selectedTextureDatas.over != null) {
                    this.view.setTexture(this.selectedTextureDatas.over.texture, this.selectedTextureDatas.over.frame);
                }
            }
        }
    }

    protected override OnPointerOut() {
        // console.log("ToggleButton::OnPointerOut");
        this.isOver = false;
        this.isDown = false;

        // default button behaviour
        if (!this.isSelected) {
            if (this.isInteractive) {
                this.view.setTexture(this.buttonTextureDatas.out.texture, this.buttonTextureDatas.out.frame);
            }
        }
        // toggle behaviour
        else {
            if (this.isInteractive) {
                if (this.selectedTextureDatas.out != null) {
                    this.view.setTexture(this.selectedTextureDatas.out.texture, this.selectedTextureDatas.out.frame);
                }
            }
        }
    }

    public override destroy(): void {
        AppDebug.log("ToggleButton(" + this.name + ")::destroy", AppDebugLogType.DESTROY);

        super.destroy();

        this.selectedTextureDatas = null;
    }
}